/**
 * Magic wand state, without React (tested in test/wand.test.ts). Mirrors apps/api/src/companion/wand.ts.
 *
 * A wand click asks admin.wand.propose for one field (or the whole coupon); every site comes back as its own
 * proposed changeset. In the editor each proposal is an entry: pending (shown inline) → accepted (its value is in
 * the site's draft) or rejected (gone). Saving a site confirms ONE of its changesets with the draft's values and
 * discards the others, so every saved site is one changeset the companion can undo.
 */

export const WAND_OPERATION = 'admin.wand.propose';
export type WandField = 'title' | 'description' | 'conditions';

export interface WandIssue {
  level: 'reject' | 'warn' | 'info';
  rule: string | null;
  message: string;
}

export interface WandFieldResult {
  field: WandField;
  current: string;
  proposed: string | null;
  reason: string;
  status: 'proposed' | 'rejected' | 'unchanged';
  issues: WandIssue[];
}

export interface WandOutput {
  configured: boolean;
  message: string | null;
  model: string | null;
  usage: { inputTokens: number; outputTokens: number; costMicroUsd: number };
  proposals: { changesetRef: string | null; rowId: string | null; siteRef: string | null; siteName: string | null; fields: WandFieldResult[] }[];
}

export interface WandInput {
  couponRef: string;
  siteRefs?: string[];
  fields: WandField[];
  note?: string;
  replaces?: string[];
}

export interface WandEntry extends WandFieldResult {
  key: string;
  /** null for the coupon's conditions (shared facts). */
  siteRef: string | null;
  changesetRef: string | null;
  rowId: string | null;
  accepted: boolean;
}

export type WandEntries = Readonly<Record<string, WandEntry>>;

/** One entry per site and field; the conditions are keyed "facts". */
export const wandKey = (siteRef: string | null, field: WandField) => `${field === 'conditions' || siteRef === null ? 'facts' : siteRef}|${field}`;

/** Entries from a wand answer. Fields the person has already typed in are skipped (`typed(siteRef, field)`). */
export function entriesFrom(output: WandOutput, typed: (siteRef: string | null, field: WandField) => boolean = () => false): WandEntry[] {
  return output.proposals.flatMap((proposal) =>
    proposal.fields
      .filter((field) => !typed(proposal.siteRef, field.field))
      .map((field) => ({ ...field, key: wandKey(proposal.siteRef, field.field), siteRef: proposal.siteRef, changesetRef: proposal.changesetRef, rowId: proposal.rowId, accepted: false })),
  );
}

export function addEntries(entries: WandEntries, added: readonly WandEntry[]): WandEntries {
  const next: Record<string, WandEntry> = { ...entries };
  for (const entry of added) next[entry.key] = entry;
  return next;
}

export function setAccepted(entries: WandEntries, key: string): WandEntries {
  const entry = entries[key];
  if (!entry || entry.status !== 'proposed') return entries;
  return { ...entries, [key]: { ...entry, accepted: true } };
}

export function removeEntry(entries: WandEntries, key: string): WandEntries {
  if (!entries[key]) return entries;
  const next: Record<string, WandEntry> = { ...entries };
  delete next[key];
  return next;
}

/** Entries of one site (or "facts" for the conditions). */
export const entriesFor = (entries: WandEntries, siteRef: string | null): WandEntry[] => Object.values(entries).filter((entry) => (siteRef === null ? entry.key.startsWith('facts|') : entry.siteRef === siteRef && !entry.key.startsWith('facts|')));

/** Whether another entry still needs this changeset (then rejecting one field must not discard it). */
export const refInUse = (entries: WandEntries, ref: string | null, exceptKey: string) => ref !== null && Object.values(entries).some((entry) => entry.key !== exceptKey && entry.changesetRef === ref);

/**
 * What saving one site (or the facts) does with its wand changesets: confirm the most recent one holding an
 * accepted proposal (with the draft's final values) and discard every other one. No accepted entry → nothing to
 * confirm; everything open is discarded and the normal save runs.
 */
export function savePlan(entries: WandEntries, siteRef: string | null): { confirm: { ref: string; rowId: string } | null; discard: string[] } {
  const mine = entriesFor(entries, siteRef);
  const accepted = mine.filter((entry) => entry.accepted && entry.changesetRef && entry.rowId);
  const chosen = accepted.at(-1);
  const confirm = chosen ? { ref: chosen.changesetRef!, rowId: chosen.rowId! } : null;
  const discard = [...new Set(mine.map((entry) => entry.changesetRef).filter((ref): ref is string => ref !== null && ref !== confirm?.ref))];
  return { confirm, discard };
}

export type WandKeyAction = 'accept' | 'reject' | 'retry' | null;

/** Keys on an inline proposal: Enter accept, Esc reject, r try again (with an optional note). */
export function wandKeyAction(event: { key: string; metaKey?: boolean; ctrlKey?: boolean; altKey?: boolean; shiftKey?: boolean }): WandKeyAction {
  if (event.metaKey || event.ctrlKey || event.altKey) return null;
  if (event.key === 'Enter' && !event.shiftKey) return 'accept';
  if (event.key === 'Escape') return 'reject';
  if (event.key === 'r' || event.key === 'R') return 'retry';
  return null;
}

/** Word-level difference for the inline view (old struck through, new marked). */
export function changedWords(from: string, to: string): { text: string; changed: boolean }[] {
  const old = new Set(from.toLowerCase().split(/\s+/));
  return to.split(/(\s+)/).map((part) => ({ text: part, changed: /\S/.test(part) && !old.has(part.toLowerCase()) }));
}

export const FIELD_LABELS: Record<WandField, string> = { title: 'title', description: 'description', conditions: 'conditions' };

/**
 * Where the wand shows (sparse): site text only on sites that show the coupon and that the person may edit;
 * conditions only when the coupon has some and the person may change shared facts. Nowhere else.
 */
export function wandPlaces(detail: { canEditFacts: boolean; coupon: { conditions: string | null }; variants: readonly { propertyRef: string; offered: boolean; canEdit: boolean }[] }): { sites: string[]; conditions: boolean } {
  return {
    sites: detail.variants.filter((variant) => variant.offered && variant.canEdit).map((variant) => variant.propertyRef),
    conditions: detail.canEditFacts && Boolean(detail.coupon.conditions?.trim()),
  };
}

/** Changesets no entry refers to any more after a change (rejected, replaced or saved): discard them. */
export function orphanedRefs(previous: WandEntries, next: WandEntries): string[] {
  const kept = new Set(Object.values(next).map((entry) => entry.changesetRef));
  return [...new Set(Object.values(previous).map((entry) => entry.changesetRef).filter((ref): ref is string => ref !== null && !kept.has(ref)))];
}

/** Fields of one site whose draft the person typed themselves (a different value than the server and than any accepted proposal). */
export function typedFields(draft: Partial<Record<WandField, string>>, server: Partial<Record<WandField, string>>, entries: readonly WandEntry[]): WandField[] {
  return (Object.keys(draft) as WandField[]).filter((field) => {
    const value = draft[field] ?? '';
    if (value === (server[field] ?? '')) return false;
    return !entries.some((entry) => entry.field === field && entry.accepted && entry.proposed === value);
  });
}
